/**
 * IconButton — square icon-only button. Same chrome variants as Button.
 * The visible glyph is decorative; supply an `aria-label` for screen readers.
 */

import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import type { ButtonVariant } from './Button';

export type IconButtonSize = 'sm' | 'md' | 'lg';

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required for screen readers — the icon itself is decorative. */
  'aria-label': string;
  children: ReactNode;
  variant?: ButtonVariant;
  size?: IconButtonSize;
  ref?: Ref<HTMLButtonElement>;
}

const VARIANT: Record<ButtonVariant, string> = {
  secondary:
    'border-line bg-paper text-ink hover:border-ink disabled:hover:border-line',
  primary:
    'border-accent-fill bg-accent-fill text-accent-ink hover:bg-accent-2 hover:border-accent-2 disabled:hover:bg-accent-fill disabled:hover:border-accent-fill',
  ink: 'border-ink bg-ink text-paper hover:bg-accent hover:border-accent disabled:hover:bg-ink disabled:hover:border-ink',
};

const SIZE: Record<IconButtonSize, string> = {
  sm: 'size-[30px]', // the height of Button `sm`, Chip and SegmentedControl, so a toolband lines up
  md: 'size-9',
  lg: 'size-11',
};

export function IconButton({
  variant = 'secondary',
  size = 'md',
  className = '',
  children,
  type = 'button',
  ref,
  ...rest
}: IconButtonProps) {
  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      className={[
        'inline-grid place-items-center border transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        SIZE[size],
        VARIANT[variant],
        className,
      ].join(' ')}
    >
      {children}
    </button>
  );
}
